import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout.js";
import { useNavigate } from "react-router-dom";

const GRADE_CONFIG = {
  excellent: { label: "Excellent", color: "#15803D", bg: "#EDFDF4" },
  good:      { label: "Good",      color: "#0369a1", bg: "#e0f2fe" },
  fair:      { label: "Fair",      color: "#B45309", bg: "#FFF7E6" },
  poor:      { label: "Poor",      color: "#DC2626", bg: "#FFF0F0" },
};

function DetailPanel({ listing, onClose, onEdit, onDelete }) {
  if (!listing) return null;

  const l = listing;
  const nv = l.variants?.find(v => v.condition === "new");
  const uv = l.variants?.find(v => v.condition === "used");
  const salePrice = nv?.price || uv?.price || 0;
  const cost = l.costPrice || 0;
  const margin = cost && salePrice ? Math.round(((salePrice - cost) / salePrice) * 100) : null;
  const stock = (nv?.stock ?? 0) + (uv?.stock ?? 0) || l.unitsAvailable || 0;
  const stockColor = stock <= 0 ? "#DC2626" : stock <= (l.lowStockThreshold || 3) ? "#B45309" : "#15803D";
  const grade = l.conditionGrade ? GRADE_CONFIG[l.conditionGrade] : null;

  const Row = ({ label, value, color }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid #f3f4f6" }}>
      <span style={{ fontSize: 13, color: "#9ca3af" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: color || "#0f0f17", textAlign: "right", maxWidth: "60%" }}>{value}</span>
    </div>
  );

  const DarkRow = ({ label, value, highlight }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <span style={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: highlight || "#fff", textAlign: "right", maxWidth: "60%" }}>{value}</span>
    </div>
  );

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 200 }} />

      {/* Panel */}
      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 420, background: "#F4F5F7", zIndex: 201, overflowY: "auto", boxShadow: "-8px 0 40px rgba(0,0,0,0.12)" }}>

        {/* Header */}
        <div style={{ background: "#fff", padding: "20px 24px", borderBottom: "1px solid #f3f4f6", position: "sticky", top: 0, zIndex: 10 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#374151" }}>✕ Close</button>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onEdit} style={{ padding: "6px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#374151" }}>Edit</button>
              <button onClick={onDelete} style={{ padding: "6px 14px", border: "1px solid #fecaca", borderRadius: 8, background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#DC2626" }}>Delete</button>
            </div>
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0f0f17", marginTop: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.title}</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{l.listingId}</div>
        </div>

        <div style={{ padding: "16px 20px" }}>

          {/* Image + quick stats */}
          <div style={{ background: "#fff", borderRadius: 16, marginBottom: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex" }}>
              <img src={l.images?.[0] || ""} alt={l.title} style={{ width: "40%", aspectRatio: "1", objectFit: "contain", background: "#f9fafb", padding: 12, boxSizing: "border-box" }} />
              <div style={{ flex: 1, padding: "16px 14px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 }}>Sale Price</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: "#0f0f17", letterSpacing: -0.5 }}>${Number(salePrice).toFixed(2)}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {nv && <span style={{ fontSize: 11, fontWeight: 700, background: "#e0f2fe", color: "#0369a1", padding: "2px 7px", borderRadius: 999 }}>New ${nv.price}</span>}
                  {uv && <span style={{ fontSize: 11, fontWeight: 700, background: "#f3f4f6", color: "#6b7280", padding: "2px 7px", borderRadius: 999 }}>Used ${uv.price}</span>}
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: stockColor }}>{stock} units</div>
                {grade && <span style={{ fontSize: 11, fontWeight: 700, background: grade.bg, color: grade.color, padding: "3px 9px", borderRadius: 999, alignSelf: "flex-start" }}>{grade.label}</span>}
              </div>
            </div>
            {l.images?.length > 1 && (
              <div style={{ display: "flex", gap: 8, padding: "0 12px 12px", overflowX: "auto", scrollbarWidth: "none" }}>
                {l.images.slice(1).map((img, i) => (
                  <img key={i} src={img} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: "contain", background: "#f3f4f6", flexShrink: 0 }} />
                ))}
              </div>
            )}
          </div>

          {/* Public Info */}
          <div style={{ background: "#fff", borderRadius: 16, marginBottom: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ padding: "12px 16px 4px", fontSize: 11, fontWeight: 800, color: "#9ca3af", letterSpacing: 1.5, textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>Product Info</div>
            <div style={{ padding: "0 16px 8px" }}>
              <Row label="Part #" value={l.partNumber || "—"} />
              <Row label="EID" value={l.eid || "—"} />
              <Row label="Category" value={l.category} />
              <Row label="Condition" value={l.condition === "new" ? "Brand New" : "Used"} />
              <Row label="Manufacturer" value={l.manufacturer || "—"} />
              <Row label="Model" value={l.model || "—"} />
              <Row label="Year" value={l.yearStart && l.yearEnd && l.yearStart !== l.yearEnd ? `${l.yearStart}–${l.yearEnd}` : l.yearStart || "—"} />
              <Row label="Color" value={l.color || "—"} />
              <Row label="Material" value={l.material || "—"} />
              <Row label="Weight" value={l.weightValue ? `${l.weightValue} ${l.weightUnit || ""}` : "—"} />
              {l.badge && <Row label="Badge" value={l.badge} />}
            </div>
          </div>

          {/* Visibility */}
          <div style={{ background: "#fff", borderRadius: 16, marginBottom: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ padding: "12px 16px 4px", fontSize: 11, fontWeight: 800, color: "#9ca3af", letterSpacing: 1.5, textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>Visibility</div>
            <div style={{ padding: "0 16px 8px" }}>
              <Row label="Published" value={l.isPublished !== false ? "✓ Live" : "✗ Draft"} color={l.isPublished !== false ? "#15803D" : "#DC2626"} />
              <Row label="Featured" value={l.isFeatured ? "✓ Featured" : "No"} color={l.isFeatured ? "#6366f1" : "#9ca3af"} />
            </div>
          </div>

          {/* Internal — dark section */}
          <div style={{ background: "#0f0f17", borderRadius: 16, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px 10px", fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.4)", letterSpacing: 1.5, textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              Internal — Admin Only
            </div>
            <DarkRow label="SKU" value={l.sku || "—"} />
            <DarkRow label="Cost Price" value={cost ? `$${Number(cost).toFixed(2)}` : "—"} />
            <DarkRow label="Sale Price" value={salePrice ? `$${Number(salePrice).toFixed(2)}` : "—"} />
          {(() => {
  const mNew = cost && nv?.price ? Math.round(((nv.price - cost) / nv.price) * 100) : null;
  const mUsed = cost && uv?.price ? Math.round(((uv.price - cost) / uv.price) * 100) : null;
  const color = m => m === null ? undefined : m >= 40 ? "#10b981" : m >= 20 ? "#f59e0b" : "#ef4444";
  return <>
    {nv && <DarkRow label="Margin (New)" value={mNew !== null ? `${mNew}%` : "—"} highlight={color(mNew)} />}
    {uv && <DarkRow label="Margin (Used)" value={mUsed !== null ? `${mUsed}%` : "—"} highlight={color(mUsed)} />}
  </>;
})()}
            <DarkRow label="Supplier" value={l.supplier || "—"} />
            <DarkRow label="Bin Location" value={l.binLocation || "—"} />
            <DarkRow label="Low Stock Threshold" value={l.lowStockThreshold ?? 3} />
            <DarkRow label="Condition Grade" value={grade ? grade.label : "—"} />
            <DarkRow label="Alt Part Numbers" value={l.altPartNumbers?.length ? l.altPartNumbers.join(", ") : "—"} />
            {l.conditionNotes && (
              <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.8 }}>Condition Notes</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>{l.conditionNotes}</div>
              </div>
            )}
            {l.compatibilityNotes && (
              <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.8 }}>Compatibility Notes</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>{l.compatibilityNotes}</div>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}

export default function ListingsScreen() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch("https://eleven11-i6v4.onrender.com/api/listings")
      .then(res => res.json())
      .then(data => setListings(data))
      .catch(err => console.error(err));
  }, []);

  const deleteListing = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
const token = localStorage.getItem("token");
    await fetch(`https://eleven11-i6v4.onrender.com/api/listings/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` }
    });
    setListings(prev => prev.filter(l => l._id !== id));
    if (selected?._id === id) setSelected(null);
  };

  const filtered = listings.filter(l =>
    !search ||
    l.title?.toLowerCase().includes(search.toLowerCase()) ||
    l.partNumber?.toLowerCase().includes(search.toLowerCase()) ||
    l.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>

      <DetailPanel
        listing={selected}
        onClose={() => setSelected(null)}
        onEdit={() => navigate(`/admin/create-listing/${selected._id}`)}
        onDelete={() => deleteListing(selected._id)}
      />

      {/* Header */}
      <div style={styles.header}>
        <div>
          <p style={styles.kicker}>ELEVEN CONTROL CENTER</p>
          <h1 style={styles.title}>Listings</h1>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.statChip}>
            <span style={styles.statNum}>{listings.length}</span>
            <span style={styles.statLabel}>Total</span>
          </div>
          <button style={styles.addBtn} onClick={() => navigate("/admin/create-listing")}>
            + Add Listing
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div style={styles.tableSection}>

        <div style={styles.toolbar}>
          <input
            style={styles.search}
            placeholder="Search by name, part number, or category..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <p style={styles.resultCount}>{filtered.length} listing{filtered.length !== 1 ? "s" : ""}</p>
        </div>

        <div style={styles.tableHeader}>
          <div style={{ width: 56 }} />
          <div style={{ flex: 1 }}>Product</div>
          <div style={{ width: 180 }}>Price</div>
          <div style={{ width: 100, textAlign: "center" }}>Stock</div>
          <div style={{ width: 80, textAlign: "center" }}>Margin</div>
          <div style={{ width: 140, textAlign: "center" }}>Actions</div>
        </div>

        <div style={styles.tableBody}>
          {filtered.length === 0 && <p style={styles.empty}>No listings found.</p>}

          {filtered.map(listing => {
            const nv = listing.variants?.find(v => v.condition === "new");
            const uv = listing.variants?.find(v => v.condition === "used");
            const salePrice = nv?.price || uv?.price || 0;
            const cost = listing.costPrice || 0;
            const margin = cost && salePrice ? Math.round(((salePrice - cost) / salePrice) * 100) : null;
            const marginColor = margin === null ? "#9ca3af" : margin >= 40 ? "#15803D" : margin >= 20 ? "#B45309" : "#DC2626";
            const isSelected = selected?._id === listing._id;

            return (
              <div
                key={listing._id}
                style={{ ...styles.tableRow, background: isSelected ? "#f0f4ff" : "transparent", cursor: "pointer" }}
                onClick={() => setSelected(isSelected ? null : listing)}
              >
                <div style={{ width: 56 }}>
                  <img src={listing.images?.[0] ? `${listing.images[0]}` : ""} alt="part" style={styles.rowImage} />
                </div>

                <div style={{ flex: 1, overflow: "hidden" }}>
                  <p style={styles.rowTitle}>{listing.title}</p>
                  <p style={styles.rowSub}>
                    {listing.partNumber && `#${listing.partNumber}`}
                    {listing.partNumber && listing.category && " · "}
                    {listing.category}
                    {listing.sku && ` · SKU: ${listing.sku}`}
                  </p>
                </div>

                <div style={{ width: 180 }}>
                  {nv && (
                    <div style={styles.priceRow}>
                      <span style={styles.priceConditionNew}>New</span>
                      <span style={styles.priceValue}>${nv.price}</span>
                    </div>
                  )}
                  {uv && (
                    <div style={{ ...styles.priceRow, marginTop: nv ? 4 : 0 }}>
                      <span style={styles.priceConditionUsed}>Used</span>
                      <span style={styles.priceValue}>${uv.price}</span>
                    </div>
                  )}
                  {!nv && !uv && <span style={styles.priceEmpty}>—</span>}
                </div>

                <div style={{ width: 100, textAlign: "center" }}>
                  <p style={{ ...styles.stockNum, color: (listing.unitsAvailable || 0) <= 0 ? "#DC2626" : (listing.unitsAvailable || 0) <= 3 ? "#B45309" : "#15803D" }}>
                    {listing.unitsAvailable ?? 0}
                  </p>
                  <p style={styles.stockLabel}>units</p>
                </div>

          <div style={{ width: 80, textAlign: "center" }}>
  {nv && (() => {
    const m = cost && nv.price ? Math.round(((nv.price - cost) / nv.price) * 100) : null;
    return <p style={{ margin: "0 0 3px", fontSize: 12, fontWeight: 800, color: m === null ? "#9ca3af" : m >= 40 ? "#15803D" : m >= 20 ? "#B45309" : "#DC2626" }}>N {m !== null ? `${m}%` : "—"}</p>;
  })()}
  {uv && (() => {
    const m = cost && uv.price ? Math.round(((uv.price - cost) / uv.price) * 100) : null;
    return <p style={{ margin: 0, fontSize: 12, fontWeight: 800, color: m === null ? "#9ca3af" : m >= 40 ? "#15803D" : m >= 20 ? "#B45309" : "#DC2626" }}>U {m !== null ? `${m}%` : "—"}</p>;
  })()}
  {!nv && !uv && <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>—</p>}
</div>

                <div style={{ width: 140, display: "flex", gap: 8, justifyContent: "center" }} onClick={e => e.stopPropagation()}>
                  <button style={styles.editBtn} onClick={() => navigate(`/admin/create-listing/${listing._id}`)}>Edit</button>
                  <button style={styles.deleteBtn} onClick={() => deleteListing(listing._id)}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={styles.tableFooter}>
          Showing {filtered.length} of {listings.length} listings
        </div>

      </div>
    </AdminLayout>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28 },
  kicker: { margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: 2, color: "#9ca3af", textTransform: "uppercase" },
  title: { margin: "6px 0 0", fontSize: 36, fontWeight: 800, letterSpacing: -1, color: "#0f0f17" },
  headerRight: { display: "flex", alignItems: "center", gap: 12, paddingTop: 8 },
  statChip: { background: "#fff", borderRadius: 12, padding: "10px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" },
  statNum: { fontSize: 22, fontWeight: 900, color: "#0f0f17", lineHeight: 1 },
  statLabel: { fontSize: 11, color: "#9ca3af", fontWeight: 600 },
  addBtn: { padding: "12px 20px", background: "#0f0f17", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  tableSection: { background: "#fff", borderRadius: 18, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)" },
  toolbar: { padding: "16px 24px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 16 },
  search: { flex: 1, padding: "10px 14px", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 14, outline: "none" },
  resultCount: { margin: 0, fontSize: 13, color: "#9ca3af", fontWeight: 500, whiteSpace: "nowrap" },
  tableHeader: { display: "flex", alignItems: "center", gap: 16, padding: "10px 24px", background: "#f9fafb", borderBottom: "1px solid #f3f4f6", fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.8 },
  tableBody: { maxHeight: "calc(100vh - 300px)", overflowY: "auto" },
  empty: { textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 14 },
  tableRow: { display: "flex", alignItems: "center", gap: 16, padding: "14px 24px", borderBottom: "1px solid #f9fafb", transition: "background 0.15s" },
  rowImage: { width: 48, height: 48, borderRadius: 10, objectFit: "contain", background: "#f3f4f6" },
  rowTitle: { margin: 0, fontSize: 14, fontWeight: 600, color: "#0f0f17", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowSub: { margin: "3px 0 0", fontSize: 12, color: "#9ca3af" },
  priceRow: { display: "flex", alignItems: "center", gap: 8 },
  priceConditionNew: { fontSize: 10, fontWeight: 700, background: "#e0f2fe", color: "#0369a1", padding: "2px 7px", borderRadius: 999 },
  priceConditionUsed: { fontSize: 10, fontWeight: 700, background: "#f3f4f6", color: "#6b7280", padding: "2px 7px", borderRadius: 999 },
  priceValue: { fontSize: 14, fontWeight: 700, color: "#0f0f17" },
  priceEmpty: { fontSize: 14, color: "#9ca3af" },
  stockNum: { margin: 0, fontSize: 20, fontWeight: 900, lineHeight: 1 },
  stockLabel: { margin: "2px 0 0", fontSize: 11, color: "#9ca3af" },
  editBtn: { padding: "6px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "transparent", fontSize: 12, fontWeight: 600, color: "#374151", cursor: "pointer" },
  deleteBtn: { padding: "6px 14px", border: "1px solid #fecaca", borderRadius: 8, background: "transparent", fontSize: 12, fontWeight: 600, color: "#DC2626", cursor: "pointer" },
  tableFooter: { padding: "12px 24px", borderTop: "1px solid #f3f4f6", fontSize: 13, color: "#9ca3af", fontWeight: 500 }
};